import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {translateConnectionObjectToArray} from 'nutshell-core';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetEditions as GetEditionsQuery,
    GetEditionsVariables as GetEditionsQueryVariables,
    EditionFragment as Edition,
} from 'nutshell-graphql-types';

import GET_EDITIONS from '../../graphql-wrappers/graphql/queries/get-editions.graphql';
import {convertEmailEngagementStatsToGeneralStatObject} from '../../utils/helpers';

import {AudienceOutreachList} from './audience-outreach-list';

const LIMIT = 10;

type OwnProps = {
    audienceId: string;
};

export const AudienceOutreach = (props: OwnProps) => {
    const {
        data,
        error,
        loading: isLoading,
        fetchMore,
    } = useQuery<GetEditionsQuery, GetEditionsQueryVariables>(GET_EDITIONS, {
        variables: {
            first: LIMIT,
            audienceId: props.audienceId,
            statuses: ['SENT', 'SENDING', 'SCHEDULED', 'HALTED', 'IN_REVIEW'],
            campaignTypes: ['BROADCAST', 'NEWSLETTER'],
        },
        notifyOnNetworkStatusChange: true,
    });

    const editions = data ? data.editions : null;
    const recentEditions = editions ? translateConnectionObjectToArray(editions) : [];
    const formattedEditions = formatEditions(recentEditions);

    const fetchMoreRecentEditions = () => {
        return fetchMore({
            variables: {
                audienceId: props.audienceId,
                first: LIMIT,
                after: editions ? editions.pageInfo.endCursor : null,
            },
            // @ts-expect-error
            updateQuery: (
                prev: GetEditionsQuery,
                newResults: {
                    fetchMoreResult: GetEditionsQuery;
                }
            ) => {
                const oldLatestOutreach = prev.editions;
                const fetchedLatestOutreach = newResults.fetchMoreResult.editions;

                const newLatestOutreach = {
                    ...oldLatestOutreach,
                    pageInfo: fetchedLatestOutreach.pageInfo,
                    edges: oldLatestOutreach.edges.concat(fetchedLatestOutreach.edges),
                } as const;

                return {editions: newLatestOutreach};
            },
        });
    };

    return (
        <AudienceOutreachList
            editions={formattedEditions}
            // @ts-expect-error
            errorMessage={safelyGetGraphQLErrorMessage(error)}
            isLoading={isLoading}
            hasNextPage={editions ? editions.pageInfo.hasNextPage : false}
            onFetchMoreRows={fetchMoreRecentEditions}
            convertEngagementStats={convertEmailEngagementStatsToGeneralStatObject}
            outreachType='EMAIL'
        />
    );
};

const formatEditions = (publishedEditions?: Edition[] | null) => {
    // @ts-expect-error
    let formattedEditions = [];
    if (publishedEditions) {
        formattedEditions = publishedEditions.map((publishedEdition) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const {name, ...restCampaign} = publishedEdition.campaign;
            const edition = {
                ...publishedEdition,
                campaign: restCampaign,
            } as const;

            return edition;
        });
    }

    // @ts-expect-error
    return formattedEditions;
};
